import type { ReactNode } from "react";
import { allDocPages } from "./content";
import DocsLayout from "./layout";

const markdown = `
The **Model Context Protocol (MCP)** is a standardized, model-agnostic JSON tool protocol. CloudQuote exposes every API as an MCP tool, so any LLM that speaks MCP can call CloudQuote — whether it's GPT-4, Claude, an on-prem LLaMA model, or something built in-house.

If you're already familiar with the [Chat API](/docs/chat-api), MCP is the same idea, expressed in a vendor-neutral wire format.

## Why MCP

- **Multi-model compatibility.** Use the same tool surface from any MCP-capable LLM. No code changes when you swap models.
- **On-prem and air-gapped LLMs.** Keep prompts inside your network while still calling CloudQuote.
- **Low-code agents.** Drag-and-drop builders (Node-RED, Blockly, etc.) can compose MCP tool calls without writing code.
- **Reduced lock-in.** Switch LLM providers without rewriting your CloudQuote integration.

## Endpoint surface

| Method | Path | Purpose |
| :---- | :---- | :---- |
| \`GET\` | \`/mcp/listTools\` | Returns the tool registry — every CloudQuote API as an MCP \`Tool Definition\`. |
| \`POST\` | \`/mcp/invoke\` | Run a tool. The body is \`{ tool_name, arguments }\`. |

## Tool discovery

\`/mcp/listTools\` returns an array of tool definitions:

\`\`\`json
[
  {
    "tool_name": "getQuote",
    "description": "Retrieve the latest stock quote for a given ticker",
    "parameters": {
      "type": "object",
      "properties": {
        "symbol": { "type": "string" }
      },
      "required": ["symbol"]
    },
    "returns": {
      "type": "object",
      "properties": {
        "symbol": { "type": "string" },
        "price": { "type": "number" },
        "change": { "type": "number" },
        "timestamp": { "type": "string", "format": "date-time" }
      }
    },
    "example_calls": [
      { "arguments": { "symbol": "AAPL" } }
    ]
  },
  {
    "tool_name": "getNewsByTicker",
    "description": "Fetch news headlines for a ticker within a date range",
    "parameters": { "...": "..." },
    "returns": { "...": "..." }
  }
]
\`\`\`

Most MCP clients call \`listTools\` once at startup and cache the registry.

## Invoking a tool

\`\`\`http
POST /mcp/invoke HTTP/1.1
Content-Type: application/json
Authorization: Bearer YOUR_CLOUDQUOTE_TOKEN

{
  "tool_name": "getTechnicalIndicators",
  "arguments": { "symbol": "GOOG", "period": 50, "indicator": "SMA" }
}
\`\`\`

### Successful response

\`\`\`json
{
  "tool_name": "getTechnicalIndicators",
  "status": "success",
  "data": {
    "symbol": "GOOG",
    "SMA_50": 2345.67,
    "date": "2025-05-30"
  }
}
\`\`\`

### Error response

\`\`\`json
{
  "tool_name": "getPriceHistory",
  "status": "error",
  "error_message": "Unauthorized – subscription does not cover historical data"
}
\`\`\`

Errors are emitted for the same reasons as the [REST API](/docs/rest-api): missing entitlement, invalid parameters, rate limits, or transient platform issues.

## Authentication

MCP uses a \`Bearer\` token in the \`Authorization\` header. Internally this maps to your existing CloudQuote credential, so entitlement and rate-limiting rules are identical to the REST API.

## A typical MCP client loop

1. **At startup**, call \`/mcp/listTools\` and cache the registry.
2. **In your LLM prompt**, declare the tools the model can use (the format depends on the LLM).
3. **When the model emits a tool call**, forward it to \`/mcp/invoke\`.
4. **Return the response** to the model and let it continue.

Frameworks like LangChain, LlamaIndex, and the open-source MCP reference clients implement this loop for you.

## Comparison: MCP vs \`/chat\`

| | \`/chat\` | MCP |
| :---- | :---- | :---- |
| Wire format | OpenAI Chat Completions | MCP JSON-RPC-ish |
| Where the LLM runs | CloudQuote-managed | Your choice (local, hosted, hybrid) |
| Streaming | Yes (SSE) | Depends on client |
| Vendor lock-in | OpenAI-compatible only | Any MCP-capable LLM |
| Best for | Quick prototypes, web/mobile chat | On-prem, regulated environments, multi-model orgs |

## Where to go next

- See [Building AI Agents](/docs/agents) for end-to-end agent patterns over MCP.
- For the equivalent OpenAI-style endpoint, see [Chat API](/docs/chat-api).
`;

export default function McpPage(): ReactNode {
  const page = allDocPages.find(p => p.slug === "mcp")!;
  return <DocsLayout page={page} markdown={markdown}/>;
}
